import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";

import { useGoogleMaps } from "../../contexts/GoogleMapsContext";

interface GoogleAutocompleteProps {
  valueloc?: string;
  className?: string;
  placeholder?: string;
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
  onCleared: () => void;
  onBlurred?: () => void;
  disabled?: boolean;
}

export interface GoogleAutoCompleteRef {
  reset: () => void;
  setPlace: (place: google.maps.places.PlaceResult) => void;
}

const GoogleAutoComplete = forwardRef<
  GoogleAutoCompleteRef,
  GoogleAutocompleteProps
>(
  (
    {
      valueloc,
      className,
      placeholder,
      onPlaceSelected,
      onCleared,
      onBlurred,
      disabled,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const maps = useGoogleMaps();
    const onPlaceSelectedRef = useRef(onPlaceSelected);
    onPlaceSelectedRef.current = onPlaceSelected;
    const [inputValue, setInputValue] = useState<string>(valueloc || "");
    const [placeSelected, setPlaceSelected] = useState(false);
    const [validating, setValidating] = useState(false); // Prevent premature validation

    useEffect(() => {
      if (valueloc !== undefined) {
        setInputValue(valueloc);
        if (inputRef.current) {
          inputRef.current.value = valueloc; // Sync input field
        }
      }
    }, [valueloc]);

    useEffect(() => {
      if (!maps || !inputRef.current || !window.google?.maps?.places?.Autocomplete) return;
      const instance = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "in" },
      });
      const listener = instance.addListener("place_changed", () => {
        const place = instance.getPlace();
        if (place?.geometry && place.name) {
          setInputValue(place.name);
          setPlaceSelected(true);
          setValidating(false);
          onPlaceSelectedRef.current(place);
        }
      });
      return () => listener.remove();
    }, [maps]);

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      setPlaceSelected(false); // Reset place selection when user types
      if (!value) {
        onCleared(); // Notify parent on clear
      }
    };

    // Handle input blur
    const handleInputBlur = () => {
      if (validating) return; // Avoid duplicate validation during pending state

      setTimeout(() => {
        if (onBlurred && !placeSelected && inputValue.trim() === "") {
          onBlurred(); // Trigger validation if no place is selected and input is empty
        }
      }, 300); // Slight delay to ensure place_changed runs first
    };

    // Handle input focus to trigger autocomplete dropdown
    const handleInputFocus = () => {
      if (inputRef.current) {
        inputRef.current.dispatchEvent(new Event("keydown", { bubbles: true })); // Manually trigger dropdown
      }
    };

    // Clear input and reset state
    const handleClear = () => {
      setInputValue("");
      setPlaceSelected(false);
      setValidating(false); // Stop pending validation
      onCleared();
      if (inputRef.current) {
        inputRef.current.value = ""; // Clear the input field
      }
    };

    // Reset function exposed to parent
    const reset = () => {
      setInputValue("");
      setPlaceSelected(false);
      setValidating(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      onCleared();
    };

    // Expose reset method to parent
    useImperativeHandle(ref, () => ({
      reset,
      setPlace: (place: google.maps.places.PlaceResult) => {
        if (inputRef.current) {
          inputRef.current.value = place.name || "";
          setInputValue(place.name || "");
          setPlaceSelected(true);
        }
      },
    }));

    return (
      <div className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder={placeholder || "Enter Location"}
          className={`w-full pr-[33px] ${className}`}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={disabled}
        />
        {/* Clear button */}
        {!disabled && inputValue && (
          <button
            type="button"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-grayText text-[12px]"
            onClick={handleClear}
          >
            ✕
          </button>
        )}
      </div>
    );
  }
);

export default GoogleAutoComplete;
